import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Shield } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const PlatformAdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    const result = await login(email, password, null, true);
    setLoading(false);

    if (result.success) {
      toast.success('Login successful!');
      navigate('/platform');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex items-center justify-center mb-6">
          <img src={LOGO_URL} alt="Orchard LMS" className="h-12 w-auto brightness-0 invert" />
        </Link>
        <div className="flex items-center justify-center gap-2 text-white mb-4">
          <Shield className="h-5 w-5 text-secondary" />
          <span className="text-sm font-medium uppercase tracking-wide">Platform Administration</span>
        </div>
        <h2 className="text-center text-3xl font-bold tracking-tight text-white">
          Admin Sign In
        </h2>
        <p className="mt-2 text-center text-sm text-gray-400">
          Access the platform administration dashboard
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-gray-300">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@orchardlms.com"
                  className="mt-1 bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                  data-testid="platform-login-email-input"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-500"
                    data-testid="platform-login-password-input"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-secondary hover:bg-secondary/90"
                disabled={loading}
                data-testid="platform-login-submit-btn"
              >
                {loading ? 'Signing in...' : 'Sign in to Admin'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-gray-400 hover:text-white inline-flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> Back to client login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminLogin;
